import { useState } from 'react';
import Drawer from '@/components/base/Drawer';
import Badge from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import type { Customer } from '@/types';

type Tab = 'Genel' | 'Ödemeler' | 'Finans';

export default function CustomerDetail({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const { jobs, payments, requests, quotes, appointments } = useAppStore();
  const [tab, setTab] = useState<Tab>('Genel');

  const customerJobs = jobs.filter((j) => j.customerName === customer.name);
  const customerPayments = payments.filter((p) => p.customerName === customer.name);
  const customerRequests = requests.filter((r) => r.customerName === customer.name);
  const customerQuotes = quotes.filter((q) => q.customerName === customer.name);
  const customerAppointments = appointments.filter((a) => a.customerName === customer.name);

  const totalJobs = customerJobs.length;
  const totalJobValue = customerJobs.reduce((s, j) => s + j.jobValue, 0);
  const collected = customerPayments.reduce((s, p) => s + p.amount, 0);
  const openBalance = customerJobs.reduce(
    (s, j) => s + Math.max(0, j.jobValue - payments.filter((p) => p.jobId === j.id).reduce((x, p) => x + p.amount, 0)),
    0,
  );

  const financeCards = [
    { label: 'Toplam İş', value: totalJobs, isCurrency: false },
    { label: 'Toplam İş Bedeli', value: totalJobValue, isCurrency: true },
    { label: 'Tahsil Edilen', value: collected, isCurrency: true },
    { label: 'Açık Bakiye', value: openBalance, isCurrency: true },
  ];

  const tabs: Tab[] = ['Genel', 'Ödemeler', 'Finans'];

  return (
    <Drawer open onClose={onClose} title={customer.name} width="max-w-2xl">
      <div className="space-y-6">
        {/* Contact */}
        <div className="flex flex-wrap items-center gap-2">
          <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100">
            <i className="ri-phone-line" /> {customer.phone}
          </a>
          <a href={`https://wa.me/90${customer.phone.replace(/\s/g, '').slice(1)}`} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100">
            <i className="ri-whatsapp-line" /> WhatsApp
          </a>
          <span className="rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-600">{customer.region}</span>
        </div>
        <p className="text-sm text-foreground-500">{customer.address}</p>

        {/* Finance cards */}
        <div className="grid grid-cols-2 gap-3">
          {financeCards.map((c) => (
            <div key={c.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="text-xs font-semibold text-foreground-500">{c.label}</div>
              <div className="mt-1 font-heading text-xl font-bold text-foreground-950">
                {c.isCurrency ? formatTL(c.value) : c.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-background-200">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                tab === t ? 'border-accent-500 text-foreground-900' : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'Genel' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
              <span className="text-sm text-foreground-500">Son Hizmet</span>
              <span className="text-sm font-semibold text-foreground-900">{customer.lastService}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
              <span className="text-sm text-foreground-500">Toplam İş</span>
              <span className="text-sm font-semibold text-foreground-900">{customer.totalJobs}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
              <span className="text-sm text-foreground-500">Son İletişim</span>
              <span className="text-sm font-semibold text-foreground-900">{customer.lastContact}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
              <span className="text-sm text-foreground-500">Kaynak</span>
              <span className="text-sm font-semibold text-foreground-900">{customer.source}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-background-50 px-4 py-3">
              <span className="text-sm text-foreground-500">Durum</span>
              <Badge tone={customer.status === 'Aktif' ? 'green' : 'neutral'}>{customer.status}</Badge>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-lg bg-background-50 py-2"><div className="font-heading text-lg font-bold text-foreground-900">{customerRequests.length}</div><div className="text-[11px] text-foreground-500">Talep</div></div>
              <div className="rounded-lg bg-background-50 py-2"><div className="font-heading text-lg font-bold text-foreground-900">{customerQuotes.length}</div><div className="text-[11px] text-foreground-500">Teklif</div></div>
              <div className="rounded-lg bg-background-50 py-2"><div className="font-heading text-lg font-bold text-foreground-900">{customerAppointments.length}</div><div className="text-[11px] text-foreground-500">Randevu</div></div>
              <div className="rounded-lg bg-background-50 py-2"><div className="font-heading text-lg font-bold text-foreground-900">{customerJobs.length}</div><div className="text-[11px] text-foreground-500">İş</div></div>
            </div>
          </div>
        )}

        {tab === 'Ödemeler' && (
          <ul className="divide-y divide-background-100">
            {customerPayments.length === 0 ? (
              <p className="py-6 text-center text-sm text-foreground-400">Bu müşteri için ödeme kaydı yok.</p>
            ) : (
              customerPayments.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{p.description || 'Tahsilat'}</div>
                    <div className="text-xs text-foreground-500">{p.date} · {p.method}</div>
                  </div>
                  <span className="font-heading text-sm font-bold text-emerald-600">{formatTL(p.amount)}</span>
                </li>
              ))
            )}
          </ul>
        )}

        {tab === 'Finans' && (
          <div className="space-y-3">
            {customerJobs.map((j) => {
              const paid = payments.filter((p) => p.jobId === j.id).reduce((s, p) => s + p.amount, 0);
              const remaining = Math.max(0, j.jobValue - paid);
              return (
                <div key={j.id} className="rounded-lg border border-background-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground-900">{j.service}</span>
                    <Badge tone={j.status === 'Kapandı' ? 'green' : 'neutral'}>{j.status}</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                    <span>Bedel: <span className="font-semibold text-foreground-800">{formatTL(j.jobValue)}</span></span>
                    <span>Tahsil: <span className="font-semibold text-emerald-600">{formatTL(paid)}</span></span>
                    <span>Kalan: <span className={`font-semibold ${remaining > 0 ? 'text-amber-600' : 'text-foreground-500'}`}>{formatTL(remaining)}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
  );
}